import React from "react";
import { AlertTriangle, RefreshCw, X } from "lucide-react";

export interface StaleDataBannerProps {
  message?: string;
  onRetry?: () => void;
  onDismiss?: () => void;
  isRetrying?: boolean;
  className?: string;
  "data-testid"?: string;
}

export const StaleDataBanner: React.FC<StaleDataBannerProps> = ({
  message = "Unable to refresh latest data. Showing previously loaded information.",
  onRetry,
  onDismiss,
  isRetrying = false,
  className = "",
  "data-testid": testId = "stale-data-banner",
}) => {
  return (
    <div
      data-testid={testId}
      role="status"
      aria-live="polite"
      className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-amber-800 shadow-xs ${className}`}
    >
      <div className="flex items-center gap-2.5">
        <AlertTriangle size={18} className="shrink-0 text-amber-600" aria-hidden="true" />
        <span className="text-xs sm:text-sm font-medium">{message}</span>
      </div>

      <div className="flex items-center gap-2 self-end sm:self-auto">
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-amber-700 transition-colors disabled:opacity-60 cursor-pointer"
          >
            <RefreshCw size={12} className={isRetrying ? "animate-spin" : ""} aria-hidden="true" />
            <span>{isRetrying ? "Retrying..." : "Retry"}</span>
          </button>
        )}

        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss stale data notification"
            className="rounded-lg p-1 text-amber-600 hover:bg-amber-100 hover:text-amber-800 transition-colors"
          >
            <X size={15} />
          </button>
        )}
      </div>
    </div>
  );
};

export default StaleDataBanner;
